import {BillingPlanPanel} from '../../billing-plan-panel';
import {Trans} from '@ui/i18n/trans';
import {useMySubscriptions} from './requests/use-my-subscriptions';
import {Skeleton} from '@ui/skeleton/skeleton';
import {AnimatePresence, m} from 'framer-motion';
import {opacityAnimation} from '@ui/animation/opacity-animation';
import {SubscriptionItem} from './subscription-item';
import {Button} from '@ui/buttons/button';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {InvoiceViewDialog} from './invoice-view-dialog';
import {VisibilityIcon} from '@ui/icons/material/Visibility';

export function MySubscriptionPanel() {
  const query = useMySubscriptions();

  const subscriptions = query.data?.subscriptions || [];
  const totalGB = query.data?.totalGB || 0;

  return (
    <BillingPlanPanel
      title={
        <div className="flex items-center justify-between">
          <Trans message="My Subscriptions" />
          <span className="text-xs font-normal">
            <Trans message="Total Storage" />: {totalGB} GB
          </span>
        </div>
      }
    >
      <div className="max-w-full p-2">
        <AnimatePresence initial={false} mode="wait">
          {query.isLoading ? (
            <LoadingSkeleton key="loading-skeleton" />
          ) : (
            <SubscriptionTable key="subscriptions" subscriptions={subscriptions} />
          )}
        </AnimatePresence>
      </div>
    </BillingPlanPanel>
  );
}

interface SubscriptionTableProps {
  subscriptions: any[];
}
function SubscriptionTable({subscriptions}: SubscriptionTableProps) {
  if (!subscriptions?.length) {
    return (
      <m.div {...opacityAnimation} className="bg-paper rounded border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-muted/50 border-b-2 border-border">
              <tr>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground">
                  <Trans message="Package" />
                </th>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground">
                  <Trans message="Start" />
                </th>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground">
                  <Trans message="End" />
                </th>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground">
                  <Trans message="Storage" />
                </th>
                <th className="px-10 py-6 text-center text-sm font-semibold text-foreground">
                  <Trans message="Amount" />
                </th>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground">
                  <Trans message="Status" />
                </th>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground">
                  <Trans message="Invoice" />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={7} className="px-10 py-12 text-center text-muted">
                  <Trans message="No subscriptions yet" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </m.div>
    );
  }

  return (
    <>
      {/* Mobile Cards - Hidden on md and up */}
      <div className="block md:hidden space-y-4">
        {subscriptions.map(subscription => (
          <SubscriptionCard key={subscription.id} subscription={subscription} />
        ))}
      </div>

      {/* Desktop Table - Hidden on mobile */}
      <m.div {...opacityAnimation} className="hidden md:block bg-paper rounded border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-muted/50 border-b-2 border-border">
              <tr>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground min-w-[140px]">
                  <Trans message="Package" />
                </th>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground min-w-[120px]">
                  <Trans message="Start" />
                </th>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground min-w-[120px]">
                  <Trans message="End" />
                </th>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground min-w-[120px]">
                  <Trans message="Storage" />
                </th>
                <th className="px-10 py-6 text-center text-sm font-semibold text-foreground min-w-[120px]">
                  <Trans message="Amount" />
                </th>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground min-w-[120px]">
                  <Trans message="Status" />
                </th>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground min-w-[120px]">
                  <Trans message="Invoice" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {subscriptions.map(subscription => (
                <SubscriptionItem key={subscription.id} subscription={subscription} />
              ))}
            </tbody>
          </table>
        </div>
      </m.div>
    </>
  );
}

interface SubscriptionCardProps {
  subscription: {
    id: number;
    package_id?: number;
    name?: string;
    start?: string;
    end?: string;
    storage?: string | number;
    amount?: string | number;
    status?: number;
  };
}

function SubscriptionCard({subscription}: SubscriptionCardProps) {
  const isActive = subscription.status === 1;
  const hasInvoice = subscription.id != null;

  const getPackageBadgeStyle = (packageId?: number) => {
    switch (packageId) {
      case 1: return { backgroundColor: 'var(--be-primary)', color: 'var(--be-on-primary)' };
      case 2: return { backgroundColor: '#8b5cf6', color: 'white' };
      case 3: return { backgroundColor: '#10b981', color: 'white' };
      case 4: return { backgroundColor: '#ef4444', color: 'white' };
      case 9: return { backgroundColor: '#f59e0b', color: 'black' };
      default: return { backgroundColor: 'var(--be-background-chip)', color: 'var(--be-text-main)' };
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="flex flex-col space-y-4">
        {/* Header with Status and Package Badge */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground">
              <Trans message="Status" />:
            </span>
            {isActive ? (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                <Trans message="Active" />
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                <Trans message="Expired" />
              </span>
            )}
          </div>

          {isActive && subscription.package_id ? (
            <span
              className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium shadow-sm"
              style={getPackageBadgeStyle(subscription.package_id)}
            >
              {subscription.name || 'Package'}
            </span>
          ) : null}
        </div>

        {/* Subscription Details in Grid */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="space-y-1">
            <span className="font-medium text-muted-foreground">
              <Trans message="Storage" />:
            </span>
            <div className="text-foreground font-medium">
              {subscription.storage}
            </div>
          </div>

          <div className="space-y-1">
            <span className="font-medium text-muted-foreground">
              <Trans message="Amount" />:
            </span>
            <div className="text-foreground font-medium">
              £{subscription.amount}
            </div>
          </div>

          <div className="space-y-1">
            <span className="font-medium text-muted-foreground">
              <Trans message="Start" />:
            </span>
            <div className="text-foreground">
              {subscription.start}
            </div>
          </div>

          <div className="space-y-1">
            <span className="font-medium text-muted-foreground">
              <Trans message="End" />:
            </span>
            <div className="text-foreground">
              {subscription.end}
            </div>
          </div>
        </div>

        {/* Invoice Button */}
        {isActive && (
          <div className="flex justify-end pt-4 border-t border-border">
            <DialogTrigger type="modal">
              <Button
                variant="outline"
                size="sm"
                className="text-primary border-primary hover:bg-primary hover:text-on-primary transition-colors"
                startIcon={<VisibilityIcon className="text-base" />}
              >
                <Trans message="View Invoice" />
              </Button>
              <InvoiceViewDialog invoiceId={subscription.id} />
            </DialogTrigger>
          </div>
        )}
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <m.div {...opacityAnimation}>
      {/* Mobile Cards Skeleton - Hidden on md and up */}
      <div className="block md:hidden space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-card border border-border rounded-lg p-6 shadow-sm">
            <div className="flex flex-col space-y-4">
              {/* Header skeleton */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-4 w-12" />
                  <Skeleton className="h-6 w-16 rounded-full" />
                </div>
                <Skeleton className="h-6 w-20 rounded-full" />
              </div>

              {/* Details grid skeleton */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-5 w-12" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-14" />
                  <Skeleton className="h-5 w-10" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-12" />
                  <Skeleton className="h-5 w-20" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-8" />
                  <Skeleton className="h-5 w-20" />
                </div>
              </div>

              {/* Button skeleton */}
              <div className="flex justify-end pt-4 border-t border-border">
                <Skeleton className="h-8 w-28" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table Skeleton - Hidden on mobile */}
      <div className="hidden md:block bg-paper rounded border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-muted/50 border-b-2 border-border">
              <tr>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground">
                  <Trans message="Package" />
                </th>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground">
                  <Trans message="Start" />
                </th>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground">
                  <Trans message="End" />
                </th>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground">
                  <Trans message="Storage" />
                </th>
                <th className="px-10 py-6 text-center text-sm font-semibold text-foreground">
                  <Trans message="Amount" />
                </th>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground">
                  <Trans message="Status" />
                </th>
                <th className="px-10 py-6 text-left text-sm font-semibold text-foreground">
                  <Trans message="Invoice" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[1, 2, 3].map((i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-10 py-6">
                    <Skeleton className="h-6 w-20" />
                  </td>
                  <td className="px-10 py-6">
                    <Skeleton className="h-4 w-16" />
                  </td>
                  <td className="px-10 py-6">
                    <Skeleton className="h-4 w-16" />
                  </td>
                  <td className="px-10 py-6">
                    <Skeleton className="h-4 w-12" />
                  </td>
                  <td className="px-10 py-6 text-center">
                    <Skeleton className="h-4 w-10 mx-auto" />
                  </td>
                  <td className="px-10 py-6">
                    <Skeleton className="h-6 w-16 rounded-full" />
                  </td>
                  <td className="px-10 py-6">
                    <Skeleton className="h-4 w-12" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </m.div>
  );
}
